/* eslint-disable jsx-a11y/anchor-is-valid*/
import React, { useEffect, useState } from "react";
import AuthenticationPageLayout from "./AuthenticationPageLayout";
import { BaseButton, Card } from "@velocity-ehs/component-library";
import { Input } from "../../sharedComponents/inputs/Input";
import { useForm } from "react-hook-form";
import classes from "./ForgotPassword.module.scss";
import RadioButton from "../../sharedComponents/inputs/RadioButton";
import { useHistory } from "react-router";
import { createFormData, AUTH_API, getLoginUrl } from "./HelperFunctions";
import { useTranslation } from "react-i18next";

type FormFields = {
  domain: string;
  username: string;
  code: string;
  resetMethod: string;
};

const ResetMethod = {
  Email: "email",
  Phone: "phone",
};

const ForgotPassword: React.FC = () => {
  const [codeError, setCodeError] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const history = useHistory();
  const { t } = useTranslation();

  const queryString = new URLSearchParams(window.location.search);

  const {
    handleSubmit,
    control,
    reset,
    getValues,
    formState: { errors, isSubmitting, isSubmitSuccessful },
  } = useForm<FormFields>({
    mode: "onBlur",
  });

  useEffect(() => {
    reset({
      domain: queryString.get("domain") || "",
      username: "",
      code: "",
      resetMethod: "email",
    });
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  const setDomainCookie = (domain: string) => {
    document.cookie = `customer=${domain}; path=/; SameSite=Lax;`;
  };

  const onSubmit = async ({ domain, username, resetMethod }: FormFields) => {
    const queryParams = new URLSearchParams(window.location.search);
    queryParams.set("domain", domain);
    history.replace({
      pathname: window.location.pathname,
      search: queryParams.toString(),
    });
    setDomainCookie(domain);
    const endpoint =
      resetMethod === ResetMethod.Email ? "requestToken" : "requestCode";
    const formData = createFormData({ domain, username });
    try {
      await AUTH_API.post(`/resetpassword/${endpoint}`, formData);
    } catch (error) {
      console.error(error);
    }
  };

  const verifyCode = async () => {
    const code = getValues("code");
    const domain = getValues("domain");
    const username = getValues("username");
    if (!code) return;
    setIsVerifying(true);
    setCodeError("");
    const formData = createFormData({ domain, code, username });
    try {
      const response = await AUTH_API.post(
        "/resetpassword/validate/code",
        formData
      );
      if (!response.data) {
        return setCodeError(t("forgotPassword_code_verify_problem"));
      }
      goToChangePassword({ code, domain, username });
    } catch (error) {
      console.error(error);
    } finally {
      setIsVerifying(false);
    }
  };

  const goToChangePassword = ({ code, domain, username }: any) => {
    history.push(
      `/changePassword?domain=${domain}&username=${username}&code=${code}`
    );
  };

  const resetForm = () => {
    reset({
      domain: queryString.get("domain") || "",
      username: "",
      code: "",
      resetMethod: "email",
    });
    setCodeError("");
  };

  const handleLoginClick = (event: any) => {
    event.preventDefault(); // Prevent page scrolling when used with anchor element of href="#"
    const encodedDomain = encodeURIComponent(getValues("domain"));
    window.location.href = getLoginUrl(encodedDomain);
  };

  return (
    <AuthenticationPageLayout>
      <Card title={t("forgotPassword_card_title")}>
        {isSubmitSuccessful &&
          getValues("resetMethod") === ResetMethod.Email && (
            <div className={classes.formSubmitted}>
              <p>{t("forgotPassword_reset_sent_to")}</p>
              <div className={classes.buttonRow}>
                <BaseButton
                  label={t("forgotPassword_return_to_login")}
                  onClick={handleLoginClick}
                />
                <BaseButton
                  label={t("forgotPassword_reset_form")}
                  className="p-button-text"
                  onClick={resetForm}
                />
              </div>
            </div>
          )}

        {isSubmitSuccessful &&
          getValues("resetMethod") === ResetMethod.Phone && (
            <div className={classes.formSubmitted}>
              <p style={{ marginBottom: "20px" }}>
                {t("forgotPassword_enter_six_digit_code")}
              </p>
              <Input
                control={control}
                label=""
                name="code"
                placeholder="000000"
                errorMessage={codeError}
              />
              <div className={classes.buttonRow}>
                <BaseButton
                  label={t("forgotPassword_verify")}
                  onClick={verifyCode}
                  loading={isVerifying}
                />
                <BaseButton
                  label={t("forgotPassword_reset_form")}
                  className="p-button-text"
                  onClick={resetForm}
                />
              </div>
            </div>
          )}

        {!isSubmitSuccessful && (
          <form
            id="forgotPasswordForm"
            onSubmit={handleSubmit(onSubmit)}
            className={classes.forgotPasswordForm}
          >
            <p>{t("forgotPassword_select_method_to_reset")}</p>
            <Input
              control={control}
              label={t("forgotPassword_domain")}
              name="domain"
              placeholder={t("forgotPassword_domain")}
              rules={{
                required: t("forgotPassword_must_enter_domain"),
              }}
              errorMessage={errors ? errors.domain?.message : undefined}
            />
            <Input
              control={control}
              label={t("forgotPassword_username")}
              name="username"
              placeholder={t("forgotPassword_username")}
              rules={{
                required: t("forgotPassword_must_enter_username"),
              }}
              errorMessage={errors ? errors.username?.message : undefined}
            />
            <div className={classes.radioContainer}>
              <span className={classes.radioLabel}>
                {t("forgotPassword_reset_method")}
              </span>
              <RadioButton
                control={control}
                name="resetMethod"
                value="email"
                label={t("forgotPassword_email")}
              />
              <RadioButton
                control={control}
                name="resetMethod"
                value="phone"
                label={t("forgotPassword_phone")}
              />
            </div>
            <div className={classes.buttonRow}>
              <BaseButton
                form="forgotPasswordForm"
                type={"submit"}
                label={t("forgotPassword_reset_password")}
                loading={isSubmitting}
              />

              <span>{t("forgotPassword_or")}</span>

              <a href="#" onClick={handleLoginClick}>
                {t("forgotPassword_login_here")}
              </a>
            </div>
          </form>
        )}
      </Card>
    </AuthenticationPageLayout>
  );
};

export default ForgotPassword;
